import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError } from '@kira/checkpoint-shared-frontend';
import React, { useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import CounterInput from 'components/CounterInput/CounterInput';
import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import Panel from 'components/Panel/Panel';
import TextArea from 'components/TextArea/TextArea';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import useNotification from 'hooks/useNotification';
import useUpdateRegistration, {
  IStudentIdRequirementType,
} from 'hooks/useUpdateRegistration';
import { t } from 'locales/i18n';

interface StudentIdRequirementProps {
  interviewUid: string;
  data?: IStudentIdRequirementType;
}

enum FormFields {
  ALLOW_ALPHABETIC = 'allowAlphabetic',
  DESCRIPTION = 'description',
  ENABLE = 'enable',
  LABEL = 'label',
  LENGTH = 'length',
}
interface FormData {
  [FormFields.ALLOW_ALPHABETIC]: boolean;
  [FormFields.DESCRIPTION]: string;
  [FormFields.ENABLE]: boolean;
  [FormFields.LABEL]: string;
  [FormFields.LENGTH]: number;
}

const StudentIdRequirement: React.FC<StudentIdRequirementProps> = ({
  data,
  interviewUid,
}) => {
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { updateRegistration, isLoading } = useUpdateRegistration();

  const defaultFormValues = useMemo(
    () => ({
      [FormFields.ALLOW_ALPHABETIC]: data?.allow_alphabetic || false,
      [FormFields.DESCRIPTION]: data?.description || '',
      [FormFields.ENABLE]: Boolean(data),
      [FormFields.LABEL]: data?.label || '',
      [FormFields.LENGTH]: data?.length || 5,
    }),
    [data]
  );

  useEffect(() => {
    reset(defaultFormValues);
  }, [data]);

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.ALLOW_ALPHABETIC]: yup.boolean(),
        [FormFields.DESCRIPTION]: yup.string().when('enable', {
          is: true,
          then: yup
            .string()
            .required(t('Student ID description is required'))
            .max(254, t('Student ID description cannot exceed 254 characters')),
        }),
        [FormFields.ENABLE]: yup.boolean(),
        [FormFields.LABEL]: yup.string().when('enable', {
          is: true,
          then: yup
            .string()
            .required(t('Student ID label is required'))
            .max(32, t('Student ID label cannot exceed 32 characters')),
        }),
        [FormFields.LENGTH]: yup.number().min(1),
      }),
    [
      FormFields.ALLOW_ALPHABETIC,
      FormFields.DESCRIPTION,
      FormFields.ENABLE,
      FormFields.LABEL,
      FormFields.LENGTH,
    ]
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  const onSubmit: SubmitHandler<FormData> = async ({
    allowAlphabetic,
    description,
    enable,
    label,
    length,
  }) => {
    const studentIdRequirement: IStudentIdRequirementType = {
      label: label || t('Label'),
      description: description || t('Description'),
      length,
      enable,
      allow_alphabetic: allowAlphabetic,
      interview: interviewUid,
    };
    await updateRegistration(
      { studentIdRequirement },
      {
        onSuccess: (data) => {
          showSuccessNotification(
            t('Success'),
            t('Registration updated successfully')
          );
          reset({ ...data });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while updating registration'),
            error.message
          );
        },
      }
    );
  };

  const icon = <InformationCircleIcon className="mr-1 inline-block h-4 w-4" />;

  const studentIdFieldsClassName = twMerge(
    'flex',
    'w-ful',
    'overflow-hidden',
    'transition-all',
    watch(FormFields.ENABLE) ? 'h-auto' : 'h-0',
    watch(FormFields.ENABLE) ? '' : '!mt-0'
  );

  return (
    <form
      data-testid="student-id-requirement-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <fieldset>
        <Panel title={t('Student ID Settings')} isFocused={isDirty}>
          <InlineForm
            title={t('Enable Student ID')}
            description={t(
              'Require applicants to enter a custom ID during registration'
            )}
            childrenClassNames="lg:pl-16"
          >
            <Controller
              name={FormFields.ENABLE}
              control={control}
              render={({ field: { onChange, value } }) => (
                <ToggleSwitch
                  enabled={value}
                  setEnabled={onChange}
                  toggleButton="studentIdSettings"
                />
              )}
            />
          </InlineForm>
          <div className={studentIdFieldsClassName}>
            <div className="w-full space-y-6 border-l-4 border-kira-slate-200 pl-4">
              <InlineForm
                title={t('ID Length')}
                description={t('Set the required number of characters')}
              >
                <Controller
                  name={FormFields.LENGTH}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CounterInput
                      value={value}
                      setValue={onChange}
                      idLength="lengthValue"
                      plusClickerId="plusClicker"
                      minusClickerId="minusClicker"
                    />
                  )}
                />
                <FormFieldError
                  isVisible={!!errors[FormFields.LENGTH]}
                  message={errors[FormFields.LENGTH]?.message ?? ''}
                  className="mt-2"
                  dataTestId="lengthError"
                  icon={icon}
                />
              </InlineForm>
              <InlineForm
                title={t('Alphanumeric')}
                description={t(
                  'Allow applicants to enter both numbers and letters'
                )}
              >
                <Controller
                  name={FormFields.ALLOW_ALPHABETIC}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <ToggleSwitch
                      enabled={value}
                      setEnabled={onChange}
                      toggleButton="alphaNumeric"
                    />
                  )}
                />
              </InlineForm>
              <InlineForm
                title={t('Label')}
                description={t(
                  'Set a custom name for the ID. This will be visible to applicants during check-in.'
                )}
              >
                <TextArea
                  errors={errors[FormFields.LABEL]}
                  errorDataTestId="labelError"
                  id={FormFields.LABEL}
                  placeholder={t('Student ID')}
                  value={watch(FormFields.LABEL)}
                  {...register(FormFields.LABEL)}
                />
              </InlineForm>
              <InlineForm
                title={t('Description')}
                description={t(
                  'Provide additional information about the ID for applicants, including the required ID length.'
                )}
              >
                <TextArea
                  errors={errors[FormFields.DESCRIPTION]}
                  id={FormFields.DESCRIPTION}
                  errorDataTestId="descriptionError"
                  placeholder={t('Enter description')}
                  value={watch(FormFields.DESCRIPTION)}
                  {...register(FormFields.DESCRIPTION)}
                />
              </InlineForm>
            </div>
          </div>
          {isDirty && (
            <FormControls
              isLoading={isLoading}
              onCancel={() => reset(defaultFormValues)}
            />
          )}
        </Panel>
      </fieldset>
    </form>
  );
};

export default StudentIdRequirement;
